/**
 * Storage levels of a bin (FIX-318).
 *
 * A storage location has up to five levels: aisle, bay, level, shelf and bin.
 * Aisle, bay, level and shelf may be unused (count 0 on the location, NULL on
 * the bin). Bin is always used. Unused levels are left out of the bin code and
 * hidden on every screen.
 *
 * The bin code is built only here, by `formatBinAddress`, and stored on
 * `storage_bins.address`. Screens show the stored address.
 */

/** The optional levels, outermost first. Bin is always used, so it is not listed. */
export const OPTIONAL_BIN_LEVELS = ["aisle", "bay", "level", "shelf"] as const;
export type OptionalBinLevel = (typeof OPTIONAL_BIN_LEVELS)[number];

type LevelValue = number | null | undefined;

export interface BinLevels {
  aisle: LevelValue;
  bay: LevelValue;
  level: LevelValue;
  shelf: LevelValue;
  bin: number;
}

export interface StorageLayout {
  aisles: number;
  bays_per_aisle: number;
  levels_per_bay: number;
  shelves_per_level: number;
  bins_per_shelf: number;
}

const isUsed = (v: LevelValue): v is number => v !== null && v !== undefined;

/**
 * Bin code, for example `A01-B2-L3-S1-B02`. Unused levels are left out:
 * 4 bays × 3 levels × 5 bins gives `B2-L3-B02`.
 */
export function formatBinAddress(
  aisle: LevelValue,
  bay: LevelValue,
  level: LevelValue,
  shelf: LevelValue,
  bin: number,
): string {
  const parts: string[] = [];
  if (isUsed(aisle)) parts.push(`A${aisle.toString().padStart(2, "0")}`);
  if (isUsed(bay)) parts.push(`B${bay}`);
  if (isUsed(level)) parts.push(`L${level}`);
  if (isUsed(shelf)) parts.push(`S${shelf}`);
  parts.push(`B${bin.toString().padStart(2, "0")}`);
  return parts.join("-");
}

/** Reads a level count typed by an admin. Empty or invalid input means 1; 0 means "not used". */
export function parseLevelCount(raw: string, min: 0 | 1): number {
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return 1;
  return Math.max(min, n);
}

/** Number of bins a layout creates. An unused level (0) counts as 1. */
export function countLayoutBins(layout: StorageLayout): number {
  const f = (n: number) => Math.max(1, n);
  return (
    f(layout.aisles) *
    f(layout.bays_per_aisle) *
    f(layout.levels_per_bay) *
    f(layout.shelves_per_level) *
    Math.max(1, layout.bins_per_shelf)
  );
}

/** All bins of a new location. Unused levels are NULL. */
export function generateLayoutBins(layout: StorageLayout): (BinLevels & { address: string })[] {
  const range = (n: number): (number | null)[] =>
    n > 0 ? Array.from({ length: n }, (_, i) => i + 1) : [null];
  const rows: (BinLevels & { address: string })[] = [];
  for (const aisle of range(layout.aisles))
    for (const bay of range(layout.bays_per_aisle))
      for (const level of range(layout.levels_per_bay))
        for (const shelf of range(layout.shelves_per_level))
          for (let bin = 1; bin <= Math.max(1, layout.bins_per_shelf); bin++)
            rows.push({ aisle, bay, level, shelf, bin, address: formatBinAddress(aisle, bay, level, shelf, bin) });
  return rows;
}

/** The used levels of one bin, outermost first, ending with bin. */
export function binLevelEntries(b: BinLevels): { key: OptionalBinLevel | "bin"; value: number }[] {
  const out: { key: OptionalBinLevel | "bin"; value: number }[] = [];
  for (const key of OPTIONAL_BIN_LEVELS) {
    const v = b[key];
    if (isUsed(v)) out.push({ key, value: v });
  }
  out.push({ key: "bin", value: b.bin });
  return out;
}

/** True when the bin uses all five levels (every bin created before FIX-318). */
export function usesAllLevels(b: BinLevels): boolean {
  return OPTIONAL_BIN_LEVELS.every((k) => isUsed(b[k]));
}

/** Compares bins by aisle, bay, level, shelf, bin. Unused levels sort first. */
export function compareBinLevels(a: BinLevels, b: BinLevels): number {
  for (const k of OPTIONAL_BIN_LEVELS) {
    const d = (a[k] ?? 0) - (b[k] ?? 0);
    if (d) return d;
  }
  return a.bin - b.bin;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

const FOOTER_PART_DEFAULTS: Record<OptionalBinLevel | "bin", string> = {
  aisle: "Aisle {{value}}",
  bay: "Bay {{value}}",
  level: "Lvl {{value}}",
  shelf: "Shelf {{value}}",
  bin: "Bin {{value}}",
};

/**
 * Footer for a bin label (namespace "inventory"). A five-level bin keeps the
 * existing translated footer, so reprinted labels look the same as before.
 */
export function binLabelFooter(t: Translate, b: BinLevels): string {
  if (usesAllLevels(b)) {
    return t("adminLabels.footer", { aisle: b.aisle, bay: b.bay, level: b.level, shelf: b.shelf, bin: b.bin });
  }
  return binLevelEntries(b)
    .map(({ key, value }) =>
      t(`adminLabels.footerPart.${key}`, { value, defaultValue: FOOTER_PART_DEFAULTS[key] }),
    )
    .join(" · ");
}

const SUMMARY_PART_DEFAULTS: Record<keyof StorageLayout, string> = {
  aisles: "{{count}} aisles",
  bays_per_aisle: "{{count}} bays",
  levels_per_bay: "{{count}} levels",
  shelves_per_level: "{{count}} shelves",
  bins_per_shelf: "{{count}} bins",
};

/**
 * Layout summary for a location (namespace "admin"), for example
 * "4 bays × 3 levels × 5 bins = 60 bins". A five-level location keeps the
 * existing translated summary.
 */
export function storageLayoutSummary(t: Translate, layout: StorageLayout): string {
  const total = countLayoutBins(layout);
  const allUsed =
    layout.aisles > 0 && layout.bays_per_aisle > 0 && layout.levels_per_bay > 0 && layout.shelves_per_level > 0;
  if (allUsed) {
    return t("storage.binLayoutSummary", {
      aisles: layout.aisles,
      bays: layout.bays_per_aisle,
      levels: layout.levels_per_bay,
      shelves: layout.shelves_per_level,
      bins: layout.bins_per_shelf,
      total,
    });
  }
  const keys = (Object.keys(SUMMARY_PART_DEFAULTS) as (keyof StorageLayout)[]).filter(
    (k) => k === "bins_per_shelf" || layout[k] > 0,
  );
  const parts = keys.map((k) =>
    t(`storage.layoutPart.${k}`, { count: layout[k], defaultValue: SUMMARY_PART_DEFAULTS[k] }),
  );
  return `${parts.join(" × ")} = ${t("storage.layoutTotal", { count: total, defaultValue: "{{count}} bins" })}`;
}
